import { Table } from "components/atoms/Table";
import { Models } from "models/types/models.type";
import { ApiLoadingStatus, Meta } from "interfaces/api.interface";
import { TableProps } from "components/atoms/Table/types";
import { ErrorBoundary } from "components/atoms/ErrorBoundary";
import { Error } from "components/atoms/Error";
import { Column } from "react-table";
import { ReactNode } from "react";

export interface UserColumnsConfig {
  [key: string]: boolean | string[];
  columnOrder?: string[];
}

type IData<T extends object> = {
  model?: Models;
  loadingStatus: ApiLoadingStatus;
  meta?: Meta;
  columns: Column<T>[]; // Allow columns to be overriden if not the default set specified on the model
  data: T[];
  onNextPage?(offset: number): void;
  onPrevPage?(offset: number): void;
  tableRowQuantityChange?(): void;
  onItemClick?(item: T): void;
  allowExport?: boolean;
  defaultColumnSize?: {
    minWidth: number;
    width: number;
  };
  userColumns?: UserColumnsConfig;
  hideSearch?: boolean;
  hideDownload?: boolean;
  hiddenColumns?: string[];
  manualPagination?: boolean;
  footerLeftContent?: ReactNode;
  /** When false the header scrolls with the rows instead of staying pinned. */
  stickyHeader?: boolean;
  /** Rendered under "No results" when there are no rows. */
  emptyState?: ReactNode;
} & Pick<
  TableProps<T>,
  | "filters"
  | "horizontalFilters"
  | "onSearchSubmit"
  | "searchInitialValue"
  | "tableKey"
  | "onRefresh"
  | "handleColumnSort"
  | "sort"
  | "downloadOptions"
  | "TableButtons"
  | "FilterComponent"
  | "showScanAndPrint"
>;

export const Data = <T extends object>({
  onItemClick,
  onSearchSubmit,
  searchInitialValue,
  tableKey,
  onRefresh,
  data = [],
  columns,
  filters,
  horizontalFilters,
  meta = {} as Meta,
  loadingStatus,
  sort,
  handleColumnSort,
  onNextPage,
  onPrevPage,
  tableRowQuantityChange,
  allowExport,
  downloadOptions,
  TableButtons,
  FilterComponent,
  defaultColumnSize, // this doesn't work yet
  userColumns,
  hiddenColumns,
  hideSearch,
  hideDownload,
  emptyState,
  showScanAndPrint,
  manualPagination,
  footerLeftContent,
  stickyHeader,
}: IData<T>) => {
  const { limit, recordCount, offset, pageCount } = meta;

  const handleRowClick = (row: T) => {
    onItemClick && onItemClick(row);
  };

  const handleNextPage = () => {
    onNextPage && onNextPage(offset + limit);
  };

  const handlePrevPage = () => {
    onPrevPage && onPrevPage(offset - limit);
  };

  return (
    <ErrorBoundary
      fallbackRender={() => {
        return <Error />;
      }}
    >
      <Table<T>
        onNextPage={handleNextPage}
        onPreviousPage={handlePrevPage}
        data={data}
        onSearchSubmit={onSearchSubmit}
        searchInitialValue={searchInitialValue}
        tableKey={tableKey}
        onRefresh={onRefresh}
        columns={columns}
        filters={filters}
        horizontalFilters={horizontalFilters}
        manualPagination={manualPagination}
        isLoading={loadingStatus === "LOADING"}
        recordCount={recordCount}
        pageCount={pageCount}
        pageIndex={offset / limit}
        pageSize={limit}
        {...(sort && { sort })}
        {...(handleColumnSort && { handleColumnSort })}
        {...(onItemClick && { onRowClick: handleRowClick })}
        allowExport={allowExport}
        downloadOptions={downloadOptions}
        TableButtons={TableButtons}
        FilterComponent={FilterComponent}
        tableRowQuantityChange={tableRowQuantityChange}
        userColumns={userColumns}
        hiddenColumns={hiddenColumns}
        hideSearch={hideSearch}
        hideDownload={hideDownload}
        showScanAndPrint={showScanAndPrint}
        footerLeftContent={footerLeftContent}
        stickyHeader={stickyHeader}
        emptyState={emptyState}
      />
    </ErrorBoundary>
  );
};
